import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { useFetcher } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Card } from '#app/components/ui/card.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { cn } from '#app/utils/misc.tsx'

interface Message {
	id: string
	role: 'user' | 'assistant'
	content: string
	metadata?: any
	createdAt: string
}

interface ChatWidgetProps {
	initialMessages?: Message[]
	chatId?: string
}

export function ChatWidget({ initialMessages = [], chatId: initialChatId }: ChatWidgetProps) {
	const { t } = useTranslation(['app', 'common'])
	const [isOpen, setIsOpen] = useState(false)
	const [messages, setMessages] = useState<Message[]>(initialMessages)
	const [input, setInput] = useState('')
	const [chatId, setChatId] = useState<string | null>(initialChatId || null)
	const fetcher = useFetcher()
	const scrollAreaRef = useRef<HTMLDivElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	// Auto-scroll to bottom when new messages arrive
	useEffect(() => {
		if (scrollAreaRef.current) {
			scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
		}
	}, [messages])

	// Handle fetcher responses
	useEffect(() => {
		if (fetcher.data?.message) {
			setMessages(prev => [...prev, fetcher.data.message])
			if (fetcher.data.chatId) {
				setChatId(fetcher.data.chatId)
			}
		}
	}, [fetcher.data])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!input.trim() || fetcher.state !== 'idle') return

		const userMessage: Message = {
			id: `temp-${Date.now()}`,
			role: 'user',
			content: input.trim(),
			createdAt: new Date().toISOString(),
		}

		setMessages(prev => [...prev, userMessage])
		setInput('')

		// Submit to API
		void fetcher.submit(
			{
				intent: 'send-message',
				message: input.trim(),
				chatId: chatId || '',
			},
			{ method: 'post', action: '/api/chat' }
		)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSubmit(e)
		}
	}

	const isLoading = fetcher.state !== 'idle'

	return (
		<>
			{/* Chat button */}
			<Button
				onClick={() => setIsOpen(true)}
				className={cn(
					"fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg",
					"hover:scale-110 transition-transform",
					isOpen && "scale-0"
				)}
				size="icon"
			>
				<MessageCircle className="h-6 w-6" />
				<span className="sr-only">{t('app:chat.openChat')}</span>
			</Button>

			{/* Chat window */}
			<Card
				className={cn(
					"fixed bottom-6 right-6 w-96 h-[600px] flex flex-col shadow-2xl transition-all duration-300 transform origin-bottom-right",
					isOpen ? "scale-100 opacity-100" : "scale-0 opacity-0 pointer-events-none"
				)}
			>
				{/* Header */}
				<div className="flex items-center justify-between border-b p-4">
					<div className="flex items-center gap-3">
						<div className="relative">
							<Bot className="h-8 w-8 text-primary" />
							<div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-background" />
						</div>
						<div>
							<h3 className="font-semibold">{t('app:chat.assistantName')}</h3>
							<p className="text-xs text-muted-foreground">{t('app:chat.assistantStatus')}</p>
						</div>
					</div>
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setIsOpen(false)}
						className="h-8 w-8"
					>
						<X className="h-4 w-4" />
						<span className="sr-only">{t('app:chat.closeChat')}</span>
					</Button>
				</div>

				{/* Messages */}
				<ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
					{messages.length === 0 ? (
						<div className="text-center text-muted-foreground py-8">
							<Bot className="h-12 w-12 mx-auto mb-4 opacity-20" />
							<p className="text-sm">{t('app:chat.welcomeMessage')}</p>
							<p className="text-xs mt-2">{t('app:chat.exampleQuestions')}</p>
						</div>
					) : (
						<div className="space-y-4">
							{messages.map((message) => (
								<div
									key={message.id}
									className={cn(
										"flex gap-3",
										message.role === 'user' && "flex-row-reverse"
									)}
								>
									<div
										className={cn(
											"h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
											message.role === 'assistant'
												? "bg-primary text-primary-foreground"
												: "bg-muted"
										)}
									>
										{message.role === 'assistant' ? (
											<Bot className="h-4 w-4" />
										) : (
											<User className="h-4 w-4" />
										)}
									</div>
									<div
										className={cn(
											"flex-1 rounded-lg p-3 max-w-[80%]",
											message.role === 'assistant'
												? "bg-muted"
												: "bg-primary text-primary-foreground"
										)}
									>
										{message.role === 'assistant' ? (
											<div className="prose prose-sm dark:prose-invert max-w-none">
												<ReactMarkdown>{message.content}</ReactMarkdown>
											</div>
										) : (
											<p className="text-sm whitespace-pre-wrap">{message.content}</p>
										)}
										{message.metadata?.action && (
											<div className="mt-2 pt-2 border-t text-xs opacity-70">
												{t('app:chat.performedAction')}: {message.metadata.action}
											</div>
										)}
									</div>
								</div>
							))}
							{isLoading && (
								<div className="flex gap-3">
									<div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
										<Bot className="h-4 w-4" />
									</div>
									<div className="bg-muted rounded-lg p-3">
										<Loader2 className="h-4 w-4 animate-spin" />
									</div>
								</div>
							)}
						</div>
					)}
				</ScrollArea>

				{/* Input */}
				<form onSubmit={handleSubmit} className="border-t p-4">
					<div className="flex gap-2">
						<Textarea
							ref={textareaRef}
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={t('app:chat.inputPlaceholder')}
							className="resize-none"
							rows={1}
							disabled={isLoading}
						/>
						<Button
							type="submit"
							size="icon"
							disabled={!input.trim() || isLoading}
							className="flex-shrink-0"
						>
							{isLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Send className="h-4 w-4" />
							)}
							<span className="sr-only">{t('app:chat.sendMessage')}</span>
						</Button>
					</div>
					<p className="text-xs text-muted-foreground mt-2">
						{t('app:chat.disclaimer')}
					</p>
				</form>
			</Card>
		</>
	)
}